import React from "react";
import { flexRender, Row, Table as TanStackTable } from "@tanstack/react-table";
import EmptyState from "./EmptyState";

interface DataTableProps<T> {
  table: TanStackTable<T>;
  /**
   * Forces a remount when the data source swaps between the regular list and
   * search results; without it TanStack keeps stale row state.
   */
  tableKey?: string;
  isEmpty: boolean;
  /** Rendered through the shared EmptyState, which the tables bypassed with
   * an inline `py-12 text-center` div. */
  empty: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description?: string;
  };
  onRowClick?: (event: React.MouseEvent, row: T) => void;
  rowClassName?: (row: T) => string;
  /** The md:hidden card list. Table bodies and card lists render the same
   * records through different markup, so this stays per-table - but the
   * empty and loading branches must not, which is what this wrapper fixes. */
  mobile?: React.ReactNode;
}

const HEAD_CELL =
  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300";

/**
 * The dashboard's table chrome.
 *
 * The <table>/<thead>/<th>/<tbody> block below was copied verbatim into four
 * TanStack tables and hand-written in three more; the <tbody> class string
 * alone was byte-identical in all seven.
 *
 * It also fixes a real bug: every table rendered its empty state *inside*
 * the `hidden ... md:block` desktop wrapper, so an empty list showed nothing
 * at all on mobile. Here the empty branch sits outside that wrapper and
 * replaces both renderers.
 */
export function DataTable<T>({
  table,
  tableKey,
  isEmpty,
  empty,
  onRowClick,
  rowClassName,
  mobile,
}: DataTableProps<T>) {
  if (isEmpty) {
    return <EmptyState {...empty} />;
  }

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table
          key={tableKey}
          className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        >
          <thead className="bg-gray-50 dark:bg-navy-800">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} scope="col" className={HEAD_CELL}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-navy-900">
            {table.getRowModel().rows.map((row: Row<T>) => (
              <tr
                key={tableKey ? `${tableKey}-${row.id}` : row.id}
                className={`hover:bg-gray-50 dark:hover:bg-navy-800 ${
                  onRowClick ? "cursor-pointer" : ""
                } ${rowClassName ? rowClassName(row.original) : ""}`}
                onClick={
                  onRowClick
                    ? (event) => onRowClick(event, row.original)
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="whitespace-nowrap px-6 py-4 text-sm"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mobile && <div className="mt-6 space-y-4 md:hidden">{mobile}</div>}
    </>
  );
}

export default DataTable;
